import React, { useEffect, useState } from 'react'
//components
import { Button } from "../../../../components/ui/buttons/Button"
import Label from '../../../../components/ui/labels/Label'
import { SelectContainer } from '../../../../components/ui/layout/SelectContainer'
import Select from "react-select"
//hooks
import { useLoading } from '../../../../contexts/LoadingContext';
import { useError } from '../../../../contexts/ErrorContext';
import { useSuccess } from '../../../../contexts/SuccessContext';
//api
import { getPermissionGroupNamesAPI, getUsersPermissionGroupsAPI, assignPermissionGroupAPI, removePermissionGroupAPI } from "../../../../services/groupsAPI"
import { InputContainer } from '../../../../components/ui/inputs/InputContainers';

/**
 * 
 * Component for updating the user's groups
 * 
 * @param {DropdownOption | null} selectedUser - user selected in parent, looks like: {value: 1, label: "FirstName LastName"}
 * @returns {JSX.Element}
 * 
 * @category Components - Admin Dashboard
 */
export const UserGroups = ({selectedUser}) => {
    const [groupNameOptions, setGroupNameOptions] = useState([])
    const [userGroups, setUserGroups] = useState([])
    const [userGroupsOriginal, setUserGroupsOriginal] = useState([])

    const { showLoading, hideLoading } = useLoading()
    const {showError} = useError()
    const {timeoutSuccess} = useSuccess()
    
    //fetch the user's groups
    const fetchUserGroups = async(userID) => {
        showLoading("Getting user groups...")
        const response = await getUsersPermissionGroupsAPI(userID);

        if (!response.success) {
            hideLoading()

            showError(`Error getting user's groups: ${response.message}`)

            return
        }
        
        const formattedGroups = response.data.map(userGroup => ({
            value: userGroup.PermissionGroup.GroupID,
            label: userGroup.PermissionGroup.Group_Name
        }));

        setUserGroups(formattedGroups)
        setUserGroupsOriginal(formattedGroups)
        
        hideLoading()
    
    }

    //fetch all groups and format as options for select component
    const fetchGroups = async () => {
        showLoading("Getting groups...")
        const response = await getPermissionGroupNamesAPI();

        if (!response.success) {
            hideLoading()

            showError(`Error getting groups: ${response.message}`)

            return
        }
        
        const formattedGroups = response.data.map(group => ({
            value: group.GroupID,
            label: group.Group_Name
        }));


        //set options for group names and get all the group data in state
        setGroupNameOptions(formattedGroups)
        
        hideLoading()
    }

    //get all groups when component loads
    useEffect(() => {
        fetchGroups()
    }, [])

    //fetch data when selectedUser changes in the parent
    useEffect(() => {
        if(selectedUser){
            
            fetchUserGroups(selectedUser.value)

        }

    }, [selectedUser])


    const handleSubmit = async() => {
        showLoading("Submitting...")

        // get ids from current and original groups
        const groupsAssignedIds = userGroups.map(group => group.value);
        const groupsAssignedOriginalIds = userGroupsOriginal.map(group => group.value);
    
        // compare ids between current and original user groups to determine which to add and remove
        const groupsToAdd = userGroups.filter(group => !groupsAssignedOriginalIds.includes(group.value));
    
        const groupsToRemove = userGroupsOriginal.filter(group => !groupsAssignedIds.includes(group.value));

        //loop through each and call api
        if(groupsToAdd.length > 0){
            for(const group of groupsToAdd){
                const response = await assignPermissionGroupAPI(selectedUser.value, group.value)
                    
                if(!response.success){
                    hideLoading()

                    showError(`Error adding groups: ${response.message}`)
        
                    return
                }
            }
        }

        if(groupsToRemove.length > 0){
            for(const group of groupsToRemove){
                const response = await removePermissionGroupAPI(selectedUser.value, group.value)
                    
                if(!response.success){
                    hideLoading()

                    showError(`Error removing groups: ${response.message}`)
        
                    return
                }
            }
        }

        //update original state
        setUserGroupsOriginal(userGroups)
        
        hideLoading()

        //everything submitted successfully, so let user know
        timeoutSuccess("Groups saved")

    }


    return (

        <div className='mb-10'>
            <InputContainer>
                <Label htmlFor='userGroupsDropdown'>Groups assigned to user</Label>

                {/* multiselect to add/remove multiple groups */}
                <SelectContainer>
                    <Select
                        value={userGroups}
                        options={groupNameOptions}
                        name="userGroupsDropdown"
                        onChange={(x) => setUserGroups(x)}
                        menuPosition='fixed'
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        isMulti={true}
                    />
                </SelectContainer>

                <Button variant='success' onClick={() => handleSubmit()} className='max-w-max'>Update Groups</Button>

            </InputContainer>

        </div>

    )
}